import { useDefinitionConfig } from '../hooks/useCancerConfig';
import { yupResolver } from '@hookform/resolvers/yup';
import Modal from '@oncolens/oncolens-ui/lib/components/Common/Modal';
import Button from '@oncolens/oncolens-ui/lib/components/FormControls/Button';
import ButtonWithLoading from '@oncolens/oncolens-ui/lib/components/FormControls/ButtonWithLoading';
import Listbox from '@oncolens/oncolens-ui/lib/components/FormControls/Listbox';
import TextAreaInput from '@oncolens/oncolens-ui/lib/components/FormControls/TextAreaInput';
import TextInput from '@oncolens/oncolens-ui/lib/components/FormControls/TextInput';
import Typeahead from '@oncolens/oncolens-ui/lib/components/FormControls/Typeahead/Typeahead';
import { PanelDropdownItemDto } from "@oncolens/oncolens-ui/lib/components/BiomarkerResults/BiomarkerResults.api";
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FaPencilAlt, FaTrash } from 'react-icons/fa';
import { BiomarkerForCreationDto, BiomarkerForUpdateDto } from '../dtos/Biomarker';
import {
  queryBiomarkersApi,
  useCreateBiomarkerMocked,
  useDeleteBiomarkerMocked,
  useUpdateBiomarkerMocked
} from '../hooks/useBiomarkers';
import { biomarkerSchema } from '../Biomarkers/schema';
import { BiomarkerItem, useBiomarkerContext } from './PatientBiomarkerForm';

interface BiomarkerProps {
	data: BiomarkerItem;
	panelId: string;
}

const Biomarker = ({ data, panelId }: BiomarkerProps) => {
  const context = useBiomarkerContext();
  const { id } = data;
  const isEditing = context.activeIds.biomarkerId === id;
  const previouslySaved = Boolean(data.biomarkerName);

  const [isApiActionExecuting, setApiActionExecuting] = useState(false);
  const createBiomarkerMutation = useCreateBiomarkerMocked();
  const updateBiomarkerMutation = useUpdateBiomarkerMocked();
  const deleteBiomarkerMutation = useDeleteBiomarkerMocked();

  const handleApiUpdate = (formValues: BiomarkerForUpdateDto) => {
    setApiActionExecuting(true);

    updateBiomarkerMutation
      .mutateAsync({
        biomarkerId: id,
        data: formValues
      })
      .then(() => {
        context.setBiomarkerPanels((panelItems) => {
          const updatedValue = panelItems.map((item) =>
            item.panel?.id === panelId
              ? ({
                ...item,
                panel: {
                  ...item.panel,
                  biomarkers: item.panel?.biomarkers.map((biomarker) =>
                    biomarker.id === id ? { id, ...formValues } : biomarker
                  )
                }
							  } as PanelDropdownItemDto)
              : item
          );

          context.persistToForm(updatedValue);

          return updatedValue;
        });

        setApiActionExecuting(false);
        setInactive();
      })
      .catch(() => {
        setApiActionExecuting(false);
      });
  };

  const handleApiCreate = (formValues: BiomarkerForCreationDto) => {
    setApiActionExecuting(true);

    createBiomarkerMutation
      .mutateAsync({
        panelId,
        data: formValues
      })
      .then((newData) => {
        setApiActionExecuting(false);
        setInactive();
        context.setBiomarkerPanels((panelItems) => {
          const updatedValue = panelItems.map((item) =>
            item.panel?.id === panelId
              ? ({
                ...item,
                panel: {
                  ...item.panel,
                  biomarkers: item.panel?.biomarkers.map((biomarker) =>
                    biomarker.id === id ? { ...newData } : biomarker
                  )
                }
							  } as PanelDropdownItemDto)
              : item
          );

          context.persistToForm(updatedValue);

          return updatedValue;
        });
      })
      .catch(() => {
        setApiActionExecuting(false);
      });
  };

  const handleApiDelete = () => {
    deleteBiomarkerMutation
      .mutateAsync(id)
      .then(() => {
        setApiActionExecuting(false);
        setInactive();
        context.setBiomarkerPanels((panelItems) => {
          const updatedValue = panelItems.map((item) =>
            item.panel?.id === panelId
              ? {
                ...item,
                panel: {
                  ...item.panel,
                  biomarkers: item.panel?.biomarkers.filter((biomarker) => biomarker.id !== id)
                }
							  }
              : item
          );

          context.persistToForm(updatedValue);

          return updatedValue;
        });
      })
      .catch(() => {
        setApiActionExecuting(false);
      });
  };

  const handleUpdate = (formValues: any) => {
    context.setBiomarkerPanels((panelItems) => {
      const updatedValue = panelItems.map((item) =>
        item.panel?.id === panelId
          ? {
            ...item,
            panel: {
              ...item.panel,
              biomarkers: item.panel.biomarkers.map((biomarker) =>
                biomarker.id === id ? { ...data, ...formValues } : biomarker
              )
            }
					  }
          : item
      );

      context.persistToForm(updatedValue);

      return updatedValue;
    });

    setInactive();
  };

  const handleCancel = () => {
    if (!previouslySaved) {
      handleRemove();
    } else {
      setInactive();
    }
  };

  const handleRemove = () => {
    context.setBiomarkerPanels((panelItems) => {
      const updatedValue = panelItems.map((item) =>
        item.panel?.id === panelId
          ? {
            ...item,
            panel: {
              ...item.panel,
              biomarkers: item.panel?.biomarkers.filter((biomarker) => biomarker.id !== id)
            }
					  }
          : item
      );

      context.persistToForm(updatedValue);

      return updatedValue;
    });
    setInactive();
  };

  const setActive = () => {
    context.setActiveIds((previous) => ({ ...previous, biomarkerId: id }));
  };

  const setInactive = () => {
    context.setActiveIds((previous) => ({ ...previous, biomarkerId: null }));
  };

  return (
    <>
      {isEditing ? (
        <BiomarkerForm
          data={data}
          isInAutosave={context.isInAutosave}
          previouslySaved={previouslySaved}
          isApiActionExecuting={isApiActionExecuting}
          onCancel={handleCancel}
          onUpdate={handleUpdate}
          onApiCreate={handleApiCreate}
          onApiUpdate={handleApiUpdate}
        />
      ) : (
        <BiomarkerValues
          data={data}
          isInAutosave={context.isInAutosave}
          onSetActive={setActive}
          onDelete={handleApiDelete}
          onRemove={handleRemove}
        />
      )}
    </>
  );
};

interface BiomarkerValuesProps {
	data: BiomarkerItem;
	isInAutosave: boolean;
	onSetActive: () => void;
	onDelete: () => void;
	onRemove: () => void;
}

const BiomarkerValues = ({
  data,
  isInAutosave,
  onSetActive,
  onDelete,
  onRemove
}: BiomarkerValuesProps) => (
  <div
    data-cy="biomarker-values"
    className="flex flex-col h-24 p-2 pt-1 pr-1 text-base font-semibold border-2 rounded-md shadow-lg bg-oncolensBlue-100 border-oncolensBlue-400 group"
  >
    <div className="flex items-center flex-grow-0">
      <h3
        data-cy="biomarker-name"
        className="min-w-0 overflow-hidden overflow-ellipsis whitespace-nowrap"
      >
        {data.biomarkerName}
      </h3>
      <span className="flex items-center ml-auto text-sm transition-opacity sm:opacity-0 sm:group-hover:pointer-events-auto sm:group-hover:opacity-100">
        <FaPencilAlt
          data-cy="biomarker-values-edit-button"
          className="box-content p-1 ml-1 text-gray-600 transition-colors duration-200 cursor-pointer hover:text-gray-800"
          onClick={onSetActive}
        />
        {isInAutosave ? (
          <Modal
            triggerElement={
              <FaTrash className="box-content p-2 text-gray-600 transition-colors duration-200 cursor-pointer hover:text-gray-800" />
            }
          >
            <Modal.Header title="Delete Biomarker" showClose={true} />
            <Modal.Content>
              <p>Are you sure you want to delete this biomarker?</p>
            </Modal.Content>
            <Modal.Buttons
              buttons={[
                {
                  label: 'Cancel',
                  style: 'secondary',
                  color: 'gray',
                  closeAfterClick: true,
                  className: 'mr-2'
                },
                {
                  label: 'Delete',
                  style: 'primary',
                  color: 'red',
                  closeAfterClick: true,
                  onClick: () => {
                    setTimeout(onDelete, 300);
                  }
                }
              ]}
            />
            {/* TODO: Remove set timeout. right now, the modal needs to live outside the removed part of the component but also still have a trigger in the right place
                      which means that clicking 'Delete' removes the whole modal tree before the close animation can finish. */}
          </Modal>
        ) : (
          <FaTrash
            data-cy="biomarker-values-delete-button"
            className="box-content p-2 text-gray-600 transition-colors duration-200 cursor-pointer hover:text-gray-800"
            onClick={onRemove}
          />
        )}
      </span>
    </div>
    <div className="flex items-center justify-center flex-1">
      <p className="flex-1 line-clamp-2" data-cy="biomarker-value">
        {data.value}
      </p>
      <p className="flex-1 line-clamp-2" data-cy="biomarker-interpretation">
        {data.interpretation}
      </p>
    </div>
  </div>
);

interface BiomarkerFormProps {
	data: BiomarkerItem;
	previouslySaved: boolean;
	isInAutosave: boolean;
	isApiActionExecuting: boolean;
	onCancel: () => void;
	onUpdate: (formValues: any) => void;
	onApiCreate: (formValues: BiomarkerForCreationDto) => void;
	onApiUpdate: (formValues: BiomarkerForUpdateDto) => void;
}

const BiomarkerForm = ({
  data,
  previouslySaved,
  isInAutosave,
  isApiActionExecuting,
  onCancel,
  onUpdate,
  onApiCreate,
  onApiUpdate
}: BiomarkerFormProps) => {
  const {
    getValues,
    trigger,
    reset,
    control,
    formState: { errors },
    setValue
  } = useForm({
    resolver: yupResolver(biomarkerSchema),
    mode: 'onChange',
  });

  const { data: testMethodologyData } = useDefinitionConfig('Test Methodology');
  const testMethodologyDataSelectItems = testMethodologyData?.map((item) => ({
    label: item.label,
    value: item.label
  }));

  useEffect(() => {
    reset(data);
  }, []);

  const validateCreateUpdate = (createUpdateAction: () => void) => {
    trigger()
      .then((isValid) => {
        if (isValid) {
          createUpdateAction();
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleCancel = () => {
    onCancel();
    reset(data);
  };

  const handleUpdate = () => {
    validateCreateUpdate(() => {
      onUpdate(getValues());
    });
  };

  const handleApiUpdate = () => {
    validateCreateUpdate(() => {
      onApiUpdate(getValues() as BiomarkerForUpdateDto);
    });
  };

  const handleApiCreate = () => {
    validateCreateUpdate(() => {
      onApiCreate(getValues() as BiomarkerForCreationDto);
    });
  };

  return (
    <div data-cy="biomarker-form" className="grid grid-cols-3 col-span-3 gap-3">
      <div className="col-span-3">
        <h2 className="pb-0.5 pr-1 inline-block text-base font-semibold border-b-2 border-oncolensBlue-400">
          {previouslySaved ? 'Update Biomarker' : 'Add New Biomarker'}
        </h2>
      </div>
      <Controller
        name="biomarkerName"
        control={control}
        render={({ field }) => {
          return (
            <Typeahead
              id="biomarkerName"
              label="Biomarker"
              data={queryBiomarkersApi}
              className="col-span-3 sm:col-span-1"
              selected={field.value ? { label: field.value, value: field.value } : null}
              onChange={(v) => {
                setValue('biomarkerApiId', !v?.isCustom ? v?.value : null);
                field.onChange(v?.label);
              }}
              options={{
                minCharactersForRemoteFetch: 2,
                allowCustomInput: true,
                useRegexMatchReplacement: false
              }}
              required
            />
          );
        }}
      />
      <Controller
        name={'value'}
        control={control}
        render={({ field }) => (
          <TextInput
            id="value"
            label="Value"
            type="text"
            htmlName="value"
            required
            error={errors?.value?.message}
            value={field.value}
            onChange={field.onChange}
            wrapperProps={{ className: 'col-span-1' }}
          />
        )}
      />
      <Controller
        name="interpretation"
        control={control}
        render={({ field }) => (
          <TextInput
            id="interpretation"
            label="Interpretation"
            type="text"
            htmlName="interpretation"
            required
            error={errors?.interpretation?.message}
            value={field.value}
            onChange={field.onChange}
            wrapperProps={{ className: 'col-span-2 sm:col-span-1' }}
          />
        )}
      />
      <Controller
        name="testMethodology"
        control={control}
        render={({ field }) => (
          <Listbox
            label="Test Methodology"
            data={testMethodologyDataSelectItems ?? []}
            selected={field.value}
            setSelected={field.onChange}
            required
            error={errors?.testMethodology?.message}
            wrapperProps={{ className: 'col-span-3 sm:col-span-1' }}
          />
        )}
      />
      <Controller
        name="alleleFrequency"
        control={control}
        render={({ field }) => (
          <TextInput
            id="allele-frequency"
            label="Allele Frequency"
            type="text"
            htmlName="allele-frequency"
            value={field.value}
            onChange={field.onChange}
            wrapperProps={{ className: 'col-span-2 sm:col-span-1 sm:col-start-3' }}
          />
        )}
      />
      <Controller
        name="comments"
        control={control}
        render={({ field }) => (
          <TextAreaInput
            id="comments"
            label="Additional Comments"
            rows={2}
            htmlName="comments"
            placeholder="Enter additional comments"
            value={field.value}
            onChange={field.onChange}
            wrapperProps={{ className: 'col-span-3 sm:col-span-2' }}
          />
        )}
      />

      <div className="flex col-span-3 my-1 ">
        <div className="flex-1 block mr-2 sm:flex-initial sm:inline-block sm:ml-auto">
          <Button
            buttonStyle="secondary"
            onClick={handleCancel}
            buttonColor="blue"
            type="button"
            className="block rounded sm:inline-block sm:py-1"
          >
						Cancel
          </Button>
        </div>
        <div className="flex-1 block sm:flex-initial sm:inline-block">
          {isInAutosave ? (
            <ButtonWithLoading
              buttonStyle="primary"
              onClick={previouslySaved ? handleApiUpdate : handleApiCreate}
              loading={isApiActionExecuting}
              buttonColor="blue"
              className="inline-block rounded sm:px-4 sm:py-1"
            >
              {previouslySaved ? 'Update' : 'Add'}
            </ButtonWithLoading>
          ) : (
            <Button
              buttonStyle="primary"
              buttonColor="blue"
              onClick={handleUpdate}
              className="inline-block rounded sm:px-4 sm:py-1"
            >
              {previouslySaved ? 'Update' : 'Add'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export { Biomarker as default };
